import Vue from 'vue';
import _ from '../../utils';
import { IJSONSchema } from './IJSONSchema';

type PropertiesOptions = Omit<IJSONSchema, 'required' | 'properties'> & {
  required?: boolean,
  sameAs?: string
  properties?: Record<string, PropertiesOptions>,
};

const queryBuilder = {
  deepSetProperty(schema: IJSONSchema, property: string, value: Record<string, any>, setRequired: boolean = false) {
    const propertyPath = property.split('.');
    let currentNode = schema ?? {};
    const nodes = [] as IJSONSchema[];
    propertyPath.forEach((prop, i) => {
      currentNode.properties = currentNode.properties ?? {};
      currentNode.type = currentNode.type ?? 'object';
      currentNode.properties[prop] = currentNode.properties[prop] ?? {};
      nodes.push(currentNode);
      currentNode = currentNode.properties[prop] as IJSONSchema;
    });
    const lastNode = nodes[nodes.length - 1];
    const lastProp = propertyPath[propertyPath.length - 1];
    if (setRequired) {
      lastNode.required = [...(lastNode.required ?? []), lastProp];
    }
    if (value) {
      lastNode.properties![lastProp] = { ...lastNode.properties![lastProp] as {}, ...value };
    }
  },
  constValues(propValues: Record<string, any>, propsRequired: boolean = false) {
    const schema: IJSONSchema = {};
    Object.keys(propValues).forEach((key) => {
      this.deepSetProperty(schema, key, { const: propValues[key] }, propsRequired);
    });
    return schema;
  },
  if(propValues: Record<string, any>, propsRequired: boolean = true) {
    const properties = this.constValues(propValues, propsRequired);
    return { if: properties };
  },
  ifNot(propValues: Record<string, any>, propsRequired: boolean = true) {
    const properties = this.constValues(propValues, propsRequired);
    return { if: { not: properties } };
  },
  thenRequired(propNames: string[]) {
    return { then: { required: propNames } };
  },
  thenValue(propValues: Record<string, any>, propsRequired: boolean = false) {
    const properties = this.constValues(propValues, propsRequired);
    return { then: properties };
  },
  elseRequired(propNames: string[]) {
    return { else: { required: propNames } };
  },
  elseValue(propValues: Record<string, any>, propsRequired: boolean = false) {
    const properties = this.constValues(propValues, propsRequired);
    return { else: properties };
  },

  properties(properties: Record<string, PropertiesOptions>) {
    const computedRequired = [] as string[];
    Object.keys(properties).forEach((key) => {
      if (properties[key].required) {
        computedRequired.push(key);
      }
    });
    const computedProps = {} as NonNullable<IJSONSchema['properties']>;
    Object.keys(properties).forEach((key) => {
      if (properties[key].type === 'object') {
        const nestedProps = properties[key].properties;
        computedProps[key] = this.properties(nestedProps as Record<string, PropertiesOptions>);
        return;
      }
      const { required, ...filteredValue } = properties[key];
      // In nested cases this has already been converted above, so casting to any here
      computedProps[key] = filteredValue as any;
      if (filteredValue.sameAs && _.isObject(computedProps[key])) {
        (computedProps[key] as IJSONSchema).pattern = `${Vue.$validation.REF_PREFIX}${filteredValue.sameAs}`;
      }
    });
    return {
      type: 'object' as IJSONSchema['type'],
      required: computedRequired as IJSONSchema['required'],
      properties: computedProps as IJSONSchema['properties'],
    };
  },
};

export default queryBuilder;
